// ABOUTME: Authored-pixel geometry for the MOBILE reel composition (SPEC-26 Phase 4b).
// ABOUTME: Everything is in the 1216x2160 portrait space and scaled at render by `--u`.

// The mobile frames are a structurally different composition, not a scaled desktop one (SPEC-26
// OQ-2), so they get their own table rather than a set of multipliers over `railGeometry.js`.
// Verified node-by-node against 45:2600 (idle), 45:2834 (watch_360), 45:2947 (dial), 45:3062
// (exploded), 45:3179 (crown), 45:3294 (movement) and 45:3413 (collection) — every value below is
// identical across all seven frames unless noted, which is what makes it chrome rather than art.
//
// The same `--u` convention as the desktop chrome ("one authored pixel, in rendered pixels"), just
// resolved against a 1216-wide frame instead of a 3840-wide one. 1/1216 = 0.0822368vw.
//
// Both frames are 2160 tall, so a vertical offset that is the same authored number on desktop and
// mobile (the wordmark's 106.67, for one) is the same fraction of the frame on both — which is why
// this file can drive vertical positions off `--u` too, and stay proportional in the process. The
// floor keeps a 320px handset legible; the ceiling stops the composition inflating on a portrait
// tablet, where 1024/1216 would otherwise push it past its authored scale.
//
// NOTE: railGeometry.js's UI_SCALE (2026-08-27) deliberately does NOT apply here. The base scale
// came down a notch on desktop only — the complaint was about monitors; on a handset the chrome is
// already near its legibility floor and has no room to shrink.
export const MOBILE_UNIT = 'clamp(0.24px, 0.0822368vw, 0.78px)';

export const MOBILE_FRAME = { width: 1216, height: 2160 };

/** @param {number} authoredPx a length measured in the Figma 1216x2160 frame */
export const mu = (authoredPx) => `calc(${authoredPx} * var(--u))`;

/**
 * The same, with a hard floor in real pixels — for type, which stops being readable long before it
 * stops being proportional. A 320px handset resolves `--u` to 0.263, so an authored 33.6 would set
 * at 8.8px; the floor holds it at 11 and lets the surrounding box stay proportional around it.
 * CSS `max()` rather than a JS clamp because `--u` is only known to the browser.
 */
export const muMin = (authoredPx, minPx) => `max(${minPx}px, calc(${authoredPx} * var(--u)))`;

/** Floor for any text in the mobile chrome. Matches PointTag's own MIN_FONT_PX. */
export const MIN_TEXT_PX = 11;

/**
 * Physical safe areas, published as custom properties by `index.css` so they are always a valid
 * length even on a browser with no `env()` at all. Composed with an authored offset rather than
 * replacing it: the design's own inset still applies, the notch/home-indicator clearance is added
 * on top of it.
 */
export const safeTop = (authoredPx) => `calc(${authoredPx} * var(--u) + var(--reel-safe-t))`;
export const safeBottom = (authoredPx) => `calc(${authoredPx} * var(--u) + var(--reel-safe-b))`;
export const safeRight = (authoredPx) => `calc(${authoredPx} * var(--u) + var(--reel-safe-r))`;

// SPEC-62 D-1: a `top` + `bottom` pair has no term for viewport HEIGHT, so a short-but-wide window
// (devtools emulation, an iPhone-SE-class landscape) starves or erases whatever it pins — the mobile
// piece card's bug (`pieceCardLayout.js`'s `mobileCardHeight`). This is the missing term: how much
// vertical room is left once `authoredPx` of authored offsets are taken out, so a caller can floor a
// height instead of just offsetting a `bottom`.
/** Height left in the viewport once `authoredPx` of authored vertical offsets are taken out. */
export const viewportHeightLess = (authoredPx) =>
  `calc(var(--reel-vh, 100vh) - var(--reel-safe-t) - var(--reel-safe-b) - ${authoredPx} * var(--u))`;

// Top chrome — `logo+blanco+recortado 5` (45:2604 et al) and `Cookies Consetina v2` (45:2677 et al).
// The wordmark is the SAME absolute 258.67x141.33 as desktop's 45:1219; on a 1216-wide frame that is
// 21.3% of the width instead of 6.7%, which is the whole difference between the two compositions'
// treatment of it. Do not "scale it down for mobile" — it is authored large here on purpose.
export const WORDMARK = { w: 258.667, h: 141.333, top: 106.667 };

// Connection indicator (dot + Online/Connecting) — mobile only (2026-09-15). It used to mirror the
// desktop rail's dot into the top-left corner (App.js), which was fine until SPEC-57's "Views" pill
// (viewToggleGeometry.js MOBILE: leftInset 37, top 112) landed in that exact corner and the two
// overlapped. No Figma frame authors a status chip, so it now sits centred below the wordmark
// instead, using the composition's own small vertical rhythm (13.333, the menu chip's row gap) as
// the gap rather than inventing an unrelated number.
export const CONNECTION_INDICATOR_GAP = 13.333;

// The menu chip. 88x88 at x=1091 in a 1216 frame, i.e. a 37 right inset — the same inset the CC bar
// uses on the left, which is what makes the two corners read as a pair.
export const MENU_CHIP = { size: 88, top: 106, rightInset: 37 };

// Glyph inside the chip: three 39x4 white strokes at y=34/47/60 (nodes 45:3291-45:3293, exported
// and re-authored as one 39x30 SVG). Offsets are the authored ones, not a centring — the artwork
// sits 32 from the top of the chip and 26 from the bottom.
export const MENU_GLYPH = { src: '/reel-chrome/icon-menu.svg', w: 39, h: 30, left: 24, top: 32 };

// Revealed rail items use the desktop pitch: the chips are the same 88px discs, and the design has
// no mobile authoring for a stack of them (see MobileChrome's header comment).
export const MENU_PITCH = 101.333;
export const MENU_GAP = MENU_PITCH - MENU_CHIP.size; // 13.333

// Bottom edge of the icon-rail stack when every chip is visible and collapsed at once — the Ophion
// `alwaysExpanded` case (MobileChrome.js, App.js's `isOphionMobileIconRail`) drops the hamburger and
// keeps all three chips (cart/wishlist/privacy) permanently stacked, instead of the one chip every
// other tenant collapses to. Any mobile overlay anchored near the top has to clear THIS extent, not
// the single-chip one, or it collides on that tenant only (found: CollectionPieceCard's mobile PDP
// card overlapping it, 2026-09-15). Derived from the same MENU_CHIP/MENU_PITCH the rail itself uses
// so a retune of either stays in step.
export const ICON_RAIL_MAX_BOTTOM = MENU_CHIP.top + 2 * MENU_PITCH + MENU_CHIP.size; // 396.667

// The rail's chips are `.reel-touch-target` (index.css): a fixed 44 REAL px minimum hit box,
// invisibly centred on the visual chip regardless of how small `--u` has shrunk it — below a
// ~608px viewport (1216 * 0.5) every chip is under 44px and the pseudo-element hit box overshoots
// the visible disc. Reported 2026-09-15: the mobile PDP's close X, placed just past
// ICON_RAIL_MAX_BOTTOM with only MENU_GAP of clearance, still lost taps to that invisible overshoot
// on a real phone — the rail's OverlayPortal sits at z-index 80, structurally above ANY z-index the
// card can set inside its own z-21 stacking context (StageHost.js), so the fix has to be clearance,
// not stacking order. 44px real content at `--u`'s floor (0.24) is a worst case of (44 - 88*0.24)/2
// ≈ 11.9 authored px of overshoot; this rounds up well past that so it holds at every viewport width,
// not just the one it was measured on.
export const RAIL_TOUCH_OVERSHOOT_GUARD = 50;

// `Rectangle 70` (45:2603 et al) — a top scrim under the wordmark and chip, so both stay legible
// over a bright frame of film. Authored 290.67 tall (13.5% of the frame) with a linear black
// gradient that is solid-ish for its first 35.4% and gone by its foot. Present on the desktop
// frames too (45:1522, 765.33 tall) where it was never built — recorded in DESIGN.md, not fixed
// here, because desktop passed visual acceptance on 2026-08-12 and this phase is mobile.
export const TOP_SCRIM = {
  h: 290.667,
  gradient: 'linear-gradient(180deg, rgba(0,0,0,0.6) 35.366%, rgba(0,0,0,0) 100%)',
};

// Footer — `Frame 168` (the bar) and `Group 158` (the icon column), identical across all 7 frames.
// The bar's left inset (37) and the icon column's right inset (1216 - 1179 = 37) match; the 13
// between the bar's right edge (1046) and the column's left edge (1059) is the gutter.
export const FOOTER = {
  barLeft: 37,
  barW: 1009,
  barH: 247,
  barBottom: 36,          // 2160 - (1877 + 247)
  barRadius: 37,          // reel.radius.ccBarMobile — note desktop's bar is 26.667, not this
  clusterSize: 139.69,    // node 45:2606 — the Cavalry slot
  clusterLeft: 33.5,
  textLeft: 207,
  textMaxW: 760,
  iconGutter: 13,
  iconW: 120,             // same 120x117.33 boxes as desktop (45:1197 / 45:1211)
  iconH: 117.333,
  iconPitch: 130.333,     // 2007.333 - 1877
  captionFont: 37.333,    // reel.text.tagBody — both caption nodes (45:3357 / 45:3358)
};

// SPEC-73 — the manual STOP box. The icon column is exactly the bar's height with its two authored
// boxes (mic + keyboard: 2 x 117.33 + 13 = 247.67 = barH), which is what keeps it below TAG_ROW_BOTTOM
// and off the right-most point tag (that pill spans the column's own x range). A third box stacked in
// the column would top out at 36 + 378 = 414 and cover the pill, so stop takes its own slot to the
// LEFT of the column, level with the keyboard box (as on desktop, where it rides beside the
// keyboard). The bar is a flex child, so it gives up `w` and nothing else moves. `lift` is how far
// its bottom edge sits above the footer's: one box + one gutter, i.e. the keyboard's row.
export const FOOTER_STOP_SLOT = {
  w: FOOTER.iconW + FOOTER.iconGutter,
  lift: FOOTER.iconH + FOOTER.iconGutter,
};

/** Height of the icon column for `boxes` stacked boxes (mic, keyboard, and the tenant camera). */
export const footerColumnHeight = (boxes) => boxes * FOOTER.iconH + (boxes - 1) * FOOTER.iconGutter;

// Point-tag row. The design bottom-anchors a horizontal row of tags above the bar rather than
// pinning them to points on the watch, which is why mobile needs no crop-aware anchor maths at all
// — see `chapterTagAnchors.js`'s MOBILE_ANCHORS note.
//
// `bottom` is derived rather than copied: the authored row bottoms are 1847 (movement), 1847.5
// (dial), 1851 (crown) and 1854 (exploded) — four hand-placed values within 7px of each other. The
// stable relationship behind them is "one gutter above the CC bar", so that is what is encoded.
export const TAG_ROW = {
  gutterAboveBar: 28,
  sideInset: 40,          // authored 37/40/49 across chapters
  // Authored 10 on movement and dial, 17 on crown and exploded. 10 is the one that has to hold:
  // movement is the only three-tag chapter whose authored widths (338 + 372 + 406 = 1116) fill the
  // row, and 1116 + 2x10 is exactly 1216 - 2x40. Taking the wider gap instead would overflow the
  // row by 6.67 authored px on the one chapter with no slack — pinned by mobileGeometry.test.
  gap: 10,
  padding: 45,            // reel.space.tagPaddingMobile — confirmed on every tag frame
  radius: 37,             // reel.radius.pointTagMobile
  font: 37.333,           // reel.text.tagBody — the SAME absolute px as desktop, deliberately
  lineHeight: 1.1,
};

/** Distance from the frame's bottom edge to the underside of the tag row. */
export const TAG_ROW_BOTTOM = FOOTER.barBottom + FOOTER.barH + TAG_ROW.gutterAboveBar; // 311

// Below this rendered font size the tag copy stops being readable on a small handset; the row keeps
// its authored proportions above it. Mirrors PointTag's own desktop floor.
export const MIN_TAG_FONT_PX = 11;

/**
 * The menu panel.
 *
 * **The design does not author a mobile cart or favourites panel** — on mobile, as on desktop, that
 * content exists only as clipped overflow inside a "Consetina" frame and never renders on a plate.
 * What the mobile frames *do* author, in full, is the privacy panel clipped inside `Cookies
 * Consetina v2` (45:3380), and every number below is lifted from it rather than invented: a
 * 1138.667-wide panel (1032 content + 53.333 either side), 493.333x74 CTA buttons (45:3406 /
 * 45:3408), 48px control glyphs, 205.333-tall rows. Reusing the *desktop* panel's authored numbers
 * here instead would have been the wrong kind of faithful — 984 authored px is 25.6% of a desktop
 * frame and 81% of a mobile one, and its 26.667 type would set at 8.5px on a 390px handset, under
 * half the size of the point-tag copy sitting beside it.
 *
 * Type is therefore the mobile composition's own body token (37.333, the same value the tags and
 * the CC bar use) rather than the desktop panel's 26.667, with secondary lines a step down.
 *
 * The one number NOT taken verbatim is the width. The panel column right-anchors on
 * `MENU_CHIP.rightInset`, so its right edge already lands on the icon column's; taking its left
 * edge to the CC bar's inset as well makes the panel span exactly bar-left → icon-column-right,
 * which is what the client asked every mobile panel to do (2026-09-01, the same pass that put the
 * PDP spec sheet on that span — see SpecSheetOverlay's MOBILE.w). The authored 1138.667 sat
 * 3.333 authored px inside it on the left: about one device pixel, invisible on its own, but it is
 * a margin that agrees with nothing. Derived rather than written out for the same reason
 * `FOOTER.barW` is — what the frame leaves over cannot drift out of step with the frame. `pad`
 * stays the authored 53.333, so the content column absorbs the 3.333.
 */
const PANEL_W = MOBILE_FRAME.width - FOOTER.barLeft - MENU_CHIP.rightInset;   // 1142
const PANEL_PAD = 53.333;

export const MOBILE_PANEL = {
  w: PANEL_W,
  pad: PANEL_PAD,
  contentW: PANEL_W - PANEL_PAD * 2,   // 1035.333, vs the authored 1032
  radius: 40,          // reel.radius.panel
  tileGap: 13.333,
  ctaH: 74,
  ruleH: 2.667,
  rowGap: 26.667,
  heartW: 34.667,
  heartH: 31.018,
  // Gap inside a bag row's action strip (heart · − · + · ✕ — see RowActions). Wider than `rowGap`
  // on purpose, because each control's hit box is capped at the strip's own PITCH (RowActions sets
  // `--hit-w` = 100% + gap) so neighbouring boxes tile rather than overlap. The earlier value (40)
  // was justified as "~49px apart at 844px" — but 844 is a phone's HEIGHT; at a real 390px width
  // the pitch was 22.8px under 44px boxes, they overlapped by ~21px, and the later sibling painted
  // on top: the right half of "+" fired "✕" (2026-09-03). 80 gives a pitch of 35.6px at 390,
  // 29.2px at 320 and 39.3px at 430 — every one past WCAG 2.5.8's 24px minimum, and the whole
  // strip (4 × 31 + 3 × 80 = 364 authored) still sits inside a compact tile column at 320px.
  actionGap: 80,
  text: { title: 37.333, product: 37.333, price: 37.333, secondary: 33.6, button: 33.6 },
};
MOBILE_PANEL.tileW = (MOBILE_PANEL.contentW - MOBILE_PANEL.tileGap) / 2; // 509.333

// Reserved two-line height for a tile's product name — the mobile twin of railGeometry's
// NAME_LINES/NAME_BLOCK_H, added for the same client feedback (2026-09-01): a title that wrapped
// beside one that didn't dropped its price and action strip a line below its neighbour's, and the
// row read as misaligned. Same fix, same numbers: explicit line-height, a min-height of exactly two
// of those lines, a 2-line clamp on top so a long name can never overrun it.
//
// Expressed as a CSS string rather than a number because mobile type carries a real-px floor
// (`muMin`) that the box has to respect too — at a 320px handset the 37.333 authored size sets at
// the 11px floor, and a box computed off the authored value alone would be shorter than the text
// inside it. `calc(max(11px, calc(37.333 * var(--u))) * 2.4)` follows whichever term wins.
export const NAME_LINES = 2;
export const NAME_LINE_HEIGHT = 1.2;
export const nameBlockHeight = () => (
  `calc(${muMin(MOBILE_PANEL.text.product, MIN_TEXT_PX)} * ${NAME_LINES * NAME_LINE_HEIGHT})`
);

// Ceiling on an expanded menu panel's (bag/favourites/privacy) height so its frosted edge never
// reaches the CC bar footer — same overlap reported on the desktop rail (2026-08-28), same fix.
// The stack is 4 items (menu chip, then bag/favourites/privacy) and only one of the latter three
// expands at a time; reserving all three collapsed chips unconditionally (worst case: privacy, the
// last item, is open) keeps one ceiling correct no matter which is open. Both the stack's top offset
// and the footer live in the SAME 2160-tall authored frame as this file's own geometry (see the file
// banner), so everything but the real safe-area insets and the actual viewport height collapses into
// one `* var(--u)` term instead of a second unit system. `overflow-y: auto` on the caller is the
// fallback if content still exceeds it — none of the three panels' content is expected to.
const PANEL_FOOTER_GUTTER_AUTHORED = 40; // same order as railGeometry's own ceiling
const PANEL_STACK_RESERVE_AUTHORED = 3 * MENU_CHIP.size + 3 * MENU_GAP; // 304
const PANEL_MAX_H_RESERVE_AUTHORED = MENU_CHIP.top + PANEL_STACK_RESERVE_AUTHORED
  + FOOTER.barBottom + FOOTER.barH + PANEL_FOOTER_GUTTER_AUTHORED; // 733

// `--reel-vh` (index.css) is 100dvh where the browser has it and 100vh where it does not; naming
// `100dvh` directly inside this calc() made the whole declaration invalid on older engines.
export const panelMaxHeight = () => (
  `calc(var(--reel-vh, 100vh) - var(--reel-safe-t) - var(--reel-safe-b) - ${PANEL_MAX_H_RESERVE_AUTHORED} * var(--u))`
);

// The consent/privacy rows, laid out inside MOBILE_PANEL's own frosted container (45:3380 is the
// panel this whole file's numbers were lifted from in the first place — see MOBILE_PANEL's note).
// The design doesn't author separate row offsets for mobile the way desktop's 113:6517 does, so
// this reuses MOBILE_PANEL's own established spacing tokens (rowGap, ctaH) as the uniform gap
// between every section instead of inventing new pinned pixel values with no source to verify
// against.
export const MOBILE_CONSENT = {
  rowH: 205.333,          // same authored row height as desktop (45:4470 et al) — unscaled, mobile
                           // type sizes are already the composition's own body token, not desktop's
  rowRadius: MOBILE_PANEL.radius,
  rowIconSize: 48,
  rowGap: MOBILE_PANEL.rowGap,
  // No authored pinned offsets for mobile (see the note above) — every flow gap reuses the one
  // spacing token the mobile composition already establishes, uniformly.
  glyphToTitleGap: MOBILE_PANEL.rowGap,
  titleToLinkGap: MOBILE_PANEL.rowGap,
  linkToRowsGap: MOBILE_PANEL.rowGap,
  footerGap: MOBILE_PANEL.rowGap,
  footerBtnGap: MOBILE_PANEL.tileGap,
  footerH: MOBILE_PANEL.ctaH,
};

// Mobile keeps the composition's own body token throughout (see MOBILE_PANEL's note) rather than
// desktop's larger CONSENT_TEXT sizes, which would set illegibly small on a handset-width panel.
export const MOBILE_CONSENT_TEXT = {
  title: MOBILE_PANEL.text.title,
  link: MOBILE_PANEL.text.secondary,
  rowTitle: MOBILE_PANEL.text.title,
  rowBody: MOBILE_PANEL.text.secondary,
  cta: MOBILE_PANEL.text.button,
};

// Same overflow rule as the desktop panel (client feedback, 2026-08-24): tiles step down to this
// fraction of MOBILE_PANEL.tileW past ITEMS_PER_ROW items instead of the old "N more items" /
// "See full …" escalation, and the grid scrolls once it grows past ROWS_BEFORE_SCROLL rows. Type
// stays at its authored size at every step.
export const TILE_COMPACT_SCALE = 0.8;
export const ITEMS_PER_ROW = 2;
export const ROWS_BEFORE_SCROLL = 2;
// Approximate vertical extent of one tile + its name/price/heart block. Unlike the desktop figure
// this isn't read off an authored offset (the mobile frames never author a cart/favourites panel at
// all — see this file's note on MOBILE_PANEL above), so it's a sum of the block's own parts instead.
// The name term counts TWO lines since 2026-09-01: the tile now reserves a fixed two-line box for
// the title (see nameBlockHeight above), so the block is that tall whether the name wraps or not.
// Desktop needs no equivalent edit — its ITEM_BLOCK_H comes off authored offsets (nameTop/heartTop)
// that already measured a two-line name in the Figma frame.
//
// Split in two because only HALF of it scales. `TILE_COMPACT_SCALE` shrinks the artwork square and
// nothing else — "Type stays at its authored size at every step" is the rule the tile is built to
// (see MobileTile) — so multiplying the whole block by the scale, which is what a single constant
// forced the caller to do, under-measured a compact row by ~12%. Measured on a 390x844 handset:
// predicted 187px against a real 210px, i.e. 46px short over the two rows the grid sized itself
// to, which is exactly a tile's price + action strip. Callers must scale ITEM_ART_H alone.
export const ITEM_ART_H = MOBILE_PANEL.tileW;
export const ITEM_TEXT_BLOCK_H =
  MOBILE_PANEL.rowGap + MOBILE_PANEL.text.product * NAME_LINE_HEIGHT * NAME_LINES
  + MOBILE_PANEL.rowGap / 2 + MOBILE_PANEL.text.price * 1.2
  + MOBILE_PANEL.rowGap / 2 + MOBILE_PANEL.heartH;
/** One tile block at FULL size. Compact rows are `ITEM_ART_H * scale + ITEM_TEXT_BLOCK_H`. */
export const ITEM_BLOCK_H = ITEM_ART_H + ITEM_TEXT_BLOCK_H;

/**
 * The rendered size of one authored pixel, as a number rather than a CSS string — for the handful
 * of places that need to do arithmetic on it (PointTag scales its type off this) rather than hand
 * it to the browser. Mirrors `MOBILE_UNIT`'s clamp exactly; keep the two in step.
 */
export function mobileUnitFor(viewportWidth) {
  if (!viewportWidth) return 0.24;
  return Math.min(Math.max(viewportWidth / MOBILE_FRAME.width, 0.24), 0.78);
}

// ── The panel must clear the top-left hamburger (owner report 2026-10-07) ────────────────────────
// With collection_menu the hamburger sits top-left at the chip stack's mirrored inset (MENU_CHIP.rightInset), level with the bag
// panel's top. MOBILE_PANEL.w runs bar-left → icon-column-right, i.e. from that same inset, so an opened bag covered the hamburger at
// EVERY phone width. The cap starts the panel one chip gap (MENU_GAP) past the hamburger's right edge. Only applied where the
// hamburger exists; without it the panel keeps its authored width.
const HAMBURGER_CLEAR = MENU_CHIP.rightInset + MENU_CHIP.size + MENU_GAP; // 138.333 from the frame's left edge
const PANEL_SIDE_INSETS = HAMBURGER_CLEAR + MENU_CHIP.rightInset;          // 175.333: that, plus the right anchor

/**
 * `MOBILE_PANEL.w`, capped so the panel's left edge sits past the hamburger. `--reel-safe-l/r` shift the hamburger and the right
 * anchor inward, so they come off the room too. At a frame-width viewport this is MOBILE_PANEL.w minus the hamburger's 101.333.
 */
export const clearedPanelWidth = () => (
  `min(${mu(MOBILE_PANEL.w)}, calc(100vw - var(--reel-safe-l) - var(--reel-safe-r) - ${mu(PANEL_SIDE_INSETS)}))`
);

/** The same value in JS, for tests and arithmetic. @param {number} viewportWidth @param {number} unit resolved `--u`, in real px */
export function clearedPanelWidthFor(viewportWidth, unit, safeLeft = 0, safeRight = 0) {
  return Math.min(MOBILE_PANEL.w * unit, viewportWidth - safeLeft - safeRight - PANEL_SIDE_INSETS * unit);
}

/** A panel's content column for a given CSS panel width (padding off both sides). `undefined` = today's fixed authored column. */
export const mobilePanelContentWidth = (widthCss) => (
  widthCss ? `calc(${widthCss} - ${mu(MOBILE_PANEL.pad * 2)})` : mu(MOBILE_PANEL.contentW)
);
